import type { SubjectValuesPayload } from '@Application/Subjects/Actions/subjectFetched';
import type { FieldError } from '@Infra/FetchLib/ApiError';
import type FieldDraft from '@UI/Pages/Subjects/Shared/FormData/FieldDraft';
import FieldValueFormData from '@UI/Pages/Subjects/Shared/FormData/FieldValueFormData';
import SubjectFormData from '@UI/Pages/Subjects/Shared/FormData/SubjectFormData';
import type SubjectFieldDefinitionView from '@UI/Store/SubjectTypes/Views/SubjectFieldDefinitionView';
import type AboutCandidateView from '@UI/Store/Subjects/Views/AboutCandidateView';
import type SubjectValuesView from '@UI/Store/Subjects/Views/SubjectValuesView';

type SubjectSheetFormArgs = {
	readonly title: SubjectFormData;
	readonly titleServerMessage: string | null;
	readonly entries: ReadonlyMap<string, FieldValueFormData>;
	readonly about: AboutCandidateView | null;
	readonly aboutServerMessage: string | null;
};

const VALUES_ERROR_PREFIX = 'values.';

/**
 * The title, the dynamic fields and the "about" link of a dossier, as one immutable form. It is not
 * decorated: each field is validated by its own `FieldValueFormData`, so the validation follows the
 * definitions of the type.
 *
 * Server errors do not go through `updateValidationFromExternalSource`: that route calls
 * `updateFieldsValidationsResult`, which adds the field to `ignoreFields`, an object shared by every
 * instance of the class. One server error would then switch off the local validation of that field
 * on all entries. Each message is instead held by the part it concerns and cleared when it is edited.
 */
class SubjectSheetFormData {
	public readonly title: SubjectFormData;
	public readonly titleServerMessage: string | null;
	public readonly entries: ReadonlyMap<string, FieldValueFormData>;
	public readonly about: AboutCandidateView | null;
	public readonly aboutServerMessage: string | null;

	constructor(data: SubjectSheetFormArgs) {
		this.title = data.title;
		this.titleServerMessage = data.titleServerMessage;
		this.entries = data.entries;
		this.about = data.about;
		this.aboutServerMessage = data.aboutServerMessage;
		Object.freeze(this);
	}

	/** A blank sheet; `values` seeds the fields of an existing dossier. */
	static createFor(
		definitions: readonly SubjectFieldDefinitionView[],
		title = '',
		values: SubjectValuesView | null = null,
	): SubjectSheetFormData {
		const entries = new Map<string, FieldValueFormData>();

		for (const definition of definitions) {
			entries.set(
				definition.key,
				FieldValueFormData.createFor(definition, values?.valueOf(definition) ?? null),
			);
		}

		return new SubjectSheetFormData({
			title: new SubjectFormData({ title }),
			titleServerMessage: null,
			entries,
			about: null,
			aboutServerMessage: null,
		});
	}

	private with(changes: Partial<SubjectSheetFormArgs>): SubjectSheetFormData {
		return new SubjectSheetFormData({ ...this, ...changes });
	}

	setTitle(title: string): SubjectSheetFormData {
		return this.with({ title: this.title.setTitle(title), titleServerMessage: null });
	}

	setValue(key: string, value: FieldDraft): SubjectSheetFormData {
		const entry = this.entries.get(key);

		if (entry === undefined) {
			return this;
		}

		return this.with({ entries: new Map(this.entries).set(key, entry.setValue(value)) });
	}

	/** The title follows the chosen candidate unless the user typed their own. */
	setAbout(candidate: AboutCandidateView | null): SubjectSheetFormData {
		const currentTitle = this.title.title;
		const followsPreviousCandidate = currentTitle.trim() === '' || currentTitle === this.about?.name;

		if (candidate === null || !followsPreviousCandidate) {
			return this.with({ about: candidate, aboutServerMessage: null });
		}

		return this.with({
			about: candidate,
			aboutServerMessage: null,
			title: this.title.setTitle(candidate.name),
			titleServerMessage: null,
		});
	}

	showErrors(): SubjectSheetFormData {
		const entries = new Map<string, FieldValueFormData>();

		for (const [key, entry] of this.entries) {
			entries.set(key, entry.showErrors());
		}

		return this.with({ title: this.title.showErrors(), entries });
	}

	/** Routes `title`, `about` and `values.<key>`; any other field is ignored. */
	applyServerErrors(fieldErrors: readonly FieldError[]): SubjectSheetFormData {
		let next: SubjectSheetFormData = this;

		for (const { field, message } of fieldErrors) {
			next = next.applyServerError(field, message);
		}

		return next;
	}

	private applyServerError(field: string, message: string): SubjectSheetFormData {
		if (field === 'title') {
			return this.with({ titleServerMessage: message });
		}

		if (field === 'about') {
			return this.with({ aboutServerMessage: message });
		}

		const entry = field.startsWith(VALUES_ERROR_PREFIX)
			? this.entries.get(field.slice(VALUES_ERROR_PREFIX.length))
			: undefined;

		if (entry === undefined) {
			return this;
		}

		return this.with({
			entries: new Map(this.entries).set(entry.definition.key, entry.withServerMessage(message)),
		});
	}

	get titleErrorMessage(): string | undefined {
		return this.titleServerMessage ?? this.title.getValidationMessage('title');
	}

	get isTitleShownValid(): boolean {
		return this.titleServerMessage === null && this.title.isValid('title');
	}

	isFormDataValid(): boolean {
		return (
			this.title.isFormDataValid() &&
			[...this.entries.values()].every((entry) => entry.isFormDataValid())
		);
	}

	/** The `values` payload: the whole active sheet, empty fields left out, a boolean always present. */
	toValues(): SubjectValuesPayload {
		const values: Record<string, NonNullable<ReturnType<FieldValueFormData['toValue']>>> = {};

		for (const [key, entry] of this.entries) {
			const value = entry.toValue();

			if (value !== null) {
				values[key] = value;
			}
		}

		return values;
	}
}

export default SubjectSheetFormData;
